import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { MRT_Row } from "material-react-table";
import { PlaceApprovalRow, PlaceApprovalTableState } from "../place-booking-approval.component";
import { filterMRTEndDateProps } from "../booking-approval-hook.functions";

// ✅ Convert Luxon-style format to Day.js style for MUI
export const muiDateFormat = (dateFormat: string) =>
  dateFormat.replaceAll("d", "D").replaceAll("y", "Y");

interface P {
  renderButton: (params: MRT_Row<PlaceApprovalRow>["original"]) => JSX.Element | null;
  dateFormat: string;
  localeFullFormat: string;
}

export default function usePlaceApprovalColumns({ renderButton, dateFormat, localeFullFormat }: P) {
  const { t } = useTranslation();
  const weekDayArr = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return (): PlaceApprovalTableState =>
    ({
      columns: [
        { header: t("Name"), accessorKey: "userBookedFor", size: 180 },
        { accessorKey: "locationName", header: t("Location"), size: 150 },
        { accessorKey: "floorName", header: t("Floor"), size: 100 },
        { header: t("Category"), accessorKey: "workplaceCategory", size: 60 },
        {
          size: 60,
          accessorKey: "weekday",
          header: t("Weekday"),
          filterFn: "arrIncludesSome",
          filterVariant: "multi-select",
          filterSelectOptions: weekDayArr.map(day => ({
            label: t(day),
            value: t(day),
            key: day
          }))
        },
        {
          header: t("Start Date"),
          size: 200,
          sortingFn: "datetime",
          filterVariant: "date",
          filterFn: "greaterThan",
          accessorKey: "start",
          accessorFn: row => new Date(row.start),
          Cell: ({ cell }) =>
            DateTime.fromJSDate(cell.getValue() as Date).toFormat(localeFullFormat),
          muiFilterDatePickerProps: {
            format: muiDateFormat(dateFormat)
          }
        },
        {
          accessorKey: "end",
          accessorFn: row => new Date(row.end),
          header: t("End Date"),
          size: 200,
          sortingFn: "datetime",
          filterVariant: "date",
          filterFn: "lessThan",
          muiFilterDatePickerProps: ({ column }) =>
            filterMRTEndDateProps(column, muiDateFormat(dateFormat)),
          Cell: ({ cell }) =>
            DateTime.fromJSDate(cell.getValue() as Date).toFormat(localeFullFormat)
        },
        {
          header: "",
          size: 20,
          accessorKey: "renderButton",
          Cell: ({ cell }) => renderButton(cell.row.original),
          enableColumnFilter: false
        }
      ]
    }) as PlaceApprovalTableState;
}
